// MeetingStackedChart.jsx
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const EventBookingTrendChart = (meetings = [], weekStart = null, weekEnd = null) => {
  const now = dayjs();
  const startOfWeek = weekStart ? dayjs(weekStart) : now.startOf('week');
  const endOfWeek = weekEnd ? dayjs(weekEnd) : now.endOf('week');

  const xData = ['Mon', 'Tues', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const weekdayMap = {
    0: 'Sun',
    1: 'Mon',
    2: 'Tues',
    3: 'Wed',
    4: 'Thu',
    5: 'Fri',
    6: 'Sat'
  };

  const TIME_BUCKETS = {
    Morning: [6, 12],
    Afternoon: [12, 17],
    Evening: [17, 21],
    Night: [21, 24] // + 0 to 6 handled below
  };

  const initializeCounts = () => ({
    Morning: 0,
    Afternoon: 0,
    Evening: 0,
    Night: 0
  });

  const weekDayCounts = {
    Mon: initializeCounts(),
    Tues: initializeCounts(),
    Wed: initializeCounts(),
    Thu: initializeCounts(),
    Fri: initializeCounts(),
    Sat: initializeCounts(),
    Sun: initializeCounts()
  };

  const expandRecurringMeetings = (meeting, count = 4) => {
    const recurrence = (meeting.reoccurrence || '').toLowerCase();
    const start = dayjs.utc(meeting.startTime);
    const end = dayjs.utc(meeting.endTime);
    const duration = end.diff(start, 'minute');
    const events = [];

    if (recurrence === 'daily' || recurrence === '1') {
      for (let i = 0; i <= count; i++) {
        const newStart = start.add(i, 'day');
        const newEnd = newStart.add(duration, 'minute');
        events.push({ ...meeting, startTime: newStart.toISOString(), endTime: newEnd.toISOString() });
      }
    } else if (recurrence === 'weekly' || recurrence === '2') {
      for (let i = 0; i <= count; i++) {
        const newStart = start.add(i, 'week');
        const newEnd = newStart.add(duration, 'minute');
        events.push({ ...meeting, startTime: newStart.toISOString(), endTime: newEnd.toISOString() });
      }
    } else {
      events.push(meeting);
    }

    return events;
  };

  const allExpanded = meetings.flatMap(m => expandRecurringMeetings(m, 4));

  allExpanded.forEach(meeting => {
    const localStart = dayjs.utc(meeting.startTime).local();

    if (localStart.isSameOrAfter(startOfWeek) && localStart.isSameOrBefore(endOfWeek)) {
      const day = weekdayMap[localStart.day()];
      const hour = localStart.hour();

      let bucket = '';
      if (hour >= 6 && hour < 12) bucket = 'Morning';
      else if (hour >= 12 && hour < 17) bucket = 'Afternoon';
      else if (hour >= 17 && hour < 21) bucket = 'Evening';
      else bucket = 'Night';

      weekDayCounts[day][bucket]++;
    }
  });

  const timeBucketKeys = Object.keys(TIME_BUCKETS);

  const series = timeBucketKeys.map(bucket => ({
    name: bucket,
    type: 'bar',
    stack: 'time',
    barWidth: 25,
    data: xData.map(day => {
      const value = weekDayCounts[day][bucket];

      const lastVisibleBucket = timeBucketKeys
        .slice()
        .reverse()
        .find(b => weekDayCounts[day][b] > 0);

      const isTop = bucket === lastVisibleBucket;

      return {
        value,
        itemStyle: isTop ? { borderRadius: [4, 4, 0, 0] } : {}
      };
    })
  }));

  const totalPerDay = xData.map(day =>
    timeBucketKeys.reduce((sum, bucket) => sum + weekDayCounts[day][bucket], 0)
  );

  series.push({
    name: 'Total',
    type: 'line',
    data: totalPerDay,
    symbol: 'circle',
    symbolSize: 8,
    lineStyle: {
      width: 2,
      color: '#1DBA83'
    },
    itemStyle: {
      color: '#1DBA83'
    },
    tooltip: {
      valueFormatter: value => `${value} meetings`
    },
    z: 10
  });

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' }
    },
    legend: {
      show: false
    },
    xAxis: {
      type: 'category',
      data: xData,
      axisLabel: { fontWeight: 'bold' }
    },
    yAxis: {
      type: 'value',
      name: 'Meetings'
    },
    grid: {
      left: 10,
      top: 30,
      right: 10,
      bottom: 50,
      containLabel: true
    },
    series
  };
};

export default EventBookingTrendChart;